/**
 * Field: label, help and error around one control (cockatrice-ui `src/ui/Field.jsx`, ported to hyperscript;
 * PLAN.md §0).
 *
 * It gives its control an id and wires aria-describedby, aria-invalid and required,
 * so the label, help text and error are always read out with the control.
 *
 * Two ways to use it:
 *   component(Field, { label: 'Title', get children() { return component(TextField, {}); } })
 *     Cockatrice controls read the field from context
 *   component(Field, { label: 'Goal', children: (a) => el('input', spread(a, {})) })
 *     any native control: spread the attributes
 *
 * Most controls also take label / help / error directly and wrap themselves in a Field.
 */
import './css/field.css';
import { createContext, createUniqueId, useContext } from 'solid-js';
import h from 'solid-js/h';
import Icon from './Icon.ts';
import { component, el, when } from './solid.ts';
import type { Child } from './solid.ts';
import { uiText } from './text.ts';

/** Attributes a native control needs from its Field; spread them onto the control. */
export interface FieldControlAttrs {
  /** Control id, matched by the label's for. */
  readonly id: string;
  /** Error and help ids. */
  readonly 'aria-describedby'?: string | undefined;
  /** "true" when the field has an error. */
  readonly 'aria-invalid'?: 'true' | undefined;
  /** Set when the field is required. */
  readonly required?: boolean | undefined;
  /** Set when the field is disabled. */
  readonly disabled?: boolean | undefined;
}

export interface FieldProps {
  /** Visible label, tied to the control. */
  readonly label?: Child;
  /** Help text under the control. */
  readonly help?: Child;
  /** Error message; sets aria-invalid on the control. */
  readonly error?: Child;
  /** Shows "Required" and sets required on the control. */
  readonly required?: boolean | undefined;
  /** Shows "Optional". */
  readonly optional?: boolean | undefined;
  /** Hides the "Required" marker (when every field is required). */
  readonly hideMarker?: boolean | undefined;
  /** Dims the label and disables the control. */
  readonly disabled?: boolean | undefined;
  /** Label beside the control instead of above (stacks on phones). */
  readonly inline?: boolean | undefined;
  /** Control id; generated when omitted. */
  readonly id?: string | undefined;
  /** Extra class. */
  readonly class?: string | undefined;
  /** A Cockatrice control, or a function that spreads the attributes onto any control. */
  readonly children?: Child | ((attrs: FieldControlAttrs) => Child);
}

/** What useField() returns inside a Field. */
export interface FieldContextValue {
  readonly id: string;
  readonly labelId: string;
  readonly describedBy?: string | undefined;
  readonly invalid: boolean;
  readonly required: boolean;
  readonly disabled: boolean;
}

/** A type's named members without `K` and without its index signature (`Omit` on a type with one keeps only that). */
export type Without<T, K extends PropertyKey> = { [P in keyof T as string extends P ? never : P extends K ? never : P]: T[P] };

/**
 * Props as their named members only, for `mergeProps(named(rest), own)`: merged over an index signature, every
 * optional prop of `own` would be typed `unknown`. The object is the same; only its type drops the index signature.
 */
export function named<T extends object>(props: T): Without<T, never> {
  return props;
}

const FieldContext = createContext<FieldContextValue | undefined>(undefined);

/** The surrounding Field, if any: { id, labelId, describedBy, invalid, required, disabled }. */
export function useField(): FieldContextValue | undefined {
  return useContext(FieldContext);
}

const join = (...ids: readonly unknown[]): string | undefined => ids.filter((one): one is string => typeof one === 'string' && one !== '').join(' ') || undefined;

/** What a control gives useFieldAttrs: its own id, describedby, invalid, error, required and disabled. */
export interface FieldAttrsSource {
  readonly id?: unknown;
  readonly 'aria-describedby'?: unknown;
  readonly invalid?: unknown;
  readonly error?: unknown;
  readonly required?: unknown;
  readonly disabled?: unknown;
}

/** The accessors useFieldAttrs returns. */
export interface FieldAttrs {
  readonly id: () => string;
  readonly describedBy: () => string | undefined;
  readonly invalid: () => boolean;
  readonly required: () => boolean;
  readonly disabled: () => boolean;
}

/**
 * Attributes a control needs from its Field (or from its own props when there is no Field).
 * Returns accessors so they stay reactive.
 */
export function useFieldAttrs(props: FieldAttrsSource): FieldAttrs {
  const field = useField();
  const fallbackId = createUniqueId();
  return {
    id: () => (typeof props.id === 'string' ? props.id : (field?.id ?? fallbackId)),
    describedBy: () => join(field?.describedBy, props['aria-describedby']),
    invalid: () => Boolean(props.invalid) || Boolean(props.error) || field?.invalid === true,
    required: () => Boolean(props.required) || field?.required === true,
    disabled: () => Boolean(props.disabled) || field?.disabled === true,
  };
}

export default function Field(props: FieldProps): Child {
  const id = props.id ?? createUniqueId();
  const labelId = `${id}-label`;
  const helpId = `${id}-help`;
  const errorId = `${id}-error`;

  const ctx: FieldContextValue = {
    id,
    labelId,
    get describedBy() {
      return join(Boolean(props.error) && errorId, Boolean(props.help) && helpId);
    },
    get invalid() {
      return Boolean(props.error);
    },
    get required() {
      return props.required === true;
    },
    get disabled() {
      return props.disabled === true;
    },
  };

  const attrs: FieldControlAttrs = {
    get id() {
      return id;
    },
    get 'aria-describedby'() {
      return ctx.describedBy;
    },
    get 'aria-invalid'() {
      return ctx.invalid ? 'true' : undefined;
    },
    get required() {
      return ctx.required || undefined;
    },
    get disabled() {
      return ctx.disabled || undefined;
    },
  };

  const cls = (): string =>
    ['ui-field', props.inline === true && 'ui-field--inline', props.disabled === true && 'is-disabled', props.class]
      .filter(Boolean)
      .join(' ');

  return el(
    'div',
    { class: cls, 'data-invalid': () => (props.error ? '' : undefined) },
    when(
      () => Boolean(props.label),
      () =>
        el(
          'div',
          { class: 'ui-field__head' },
          el('label', { class: 'ui-field__label', id: labelId, for: id }, () => props.label),
          when(
            () => props.required === true && props.hideMarker !== true,
            () => el('span', { class: 'ui-field__marker' }, () => uiText()('ui.field.required')),
          ),
          when(
            () => props.optional === true && props.required !== true,
            () => el('span', { class: 'ui-field__marker' }, () => uiText()('ui.field.optional')),
          ),
        ),
    ),
    el(
      'div',
      { class: 'ui-field__control' },
      // Solid's Provider, called through hyperscript: its typed children are JSX elements, never a component's Child.
      h(FieldContext.Provider, { value: ctx }, () => {
        const children = props.children;
        return typeof children === 'function' ? children(attrs) : children;
      })(),
    ),
    when(
      () => Boolean(props.error),
      () => el('p', { class: 'ui-field__error', id: errorId }, component(Icon, { name: 'warn', size: 'sm' }), el('span', {}, () => props.error)),
    ),
    when(
      () => Boolean(props.help),
      () => el('p', { class: 'ui-field__help', id: helpId }, () => props.help),
    ),
  );
}

/** FieldWrap's props: a Field's, with the class for the Field as `fieldClass`. */
export interface FieldWrapProps extends FieldProps {
  readonly fieldClass?: string | undefined;
  readonly children?: Child;
}

/**
 * Wraps children in a Field only when a label, help or error is given.
 * Used by TextField, Select, Textarea and friends so they work with or without one.
 */
export function FieldWrap(props: FieldWrapProps): Child {
  return when(
    () => Boolean(props.label) || Boolean(props.help) || Boolean(props.error),
    () =>
      component(Field, {
        get id() {
          return props.id;
        },
        get label() {
          return props.label;
        },
        get help() {
          return props.help;
        },
        get error() {
          return props.error;
        },
        get required() {
          return props.required;
        },
        get optional() {
          return props.optional;
        },
        get disabled() {
          return props.disabled;
        },
        get class() {
          return props.fieldClass;
        },
        get children() {
          return props.children;
        },
      }),
    () => props.children,
  );
}

/** The props a control that wraps itself gives its FieldWrap: its own field keys, its id and disabled. */
export interface WrappedControlProps {
  readonly label?: Child;
  readonly help?: Child;
  readonly error?: Child;
  readonly required?: boolean | undefined;
  readonly optional?: boolean | undefined;
  readonly fieldClass?: string | undefined;
  readonly disabled?: boolean | undefined;
  /** The control's id, also the Field's. */
  readonly id?: unknown;
}

/**
 * A control wrapped in its FieldWrap, as the JSX's `<FieldWrap {...field} id={props.id} disabled={props.disabled}>`:
 * the field keys, id and disabled read from `props` as they change, and `control` made inside the Field.
 */
export function wrapControl(props: WrappedControlProps, control: () => Child): Child {
  return component(FieldWrap, {
    get label() {
      return props.label;
    },
    get help() {
      return props.help;
    },
    get error() {
      return props.error;
    },
    get required() {
      return props.required;
    },
    get optional() {
      return props.optional;
    },
    get fieldClass() {
      return props.fieldClass;
    },
    get id() {
      return typeof props.id === 'string' ? props.id : undefined;
    },
    get disabled() {
      return props.disabled;
    },
    get children() {
      return control();
    },
  });
}
